import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Briefcase } from "lucide-react";
import { toast } from "react-toastify";
import Button from "../../components/UI/Button";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getWorks,
  getWork,
  getCatalog,
  createWork,
  updateWork,
  deleteWork,
  clearCurrentWork,
} from "../../store/slices/worksSlice";
import { useSocketEvent, useSocketResync } from "../../hooks/useSocketEvent";
import { withVersion } from "../../utils/concurrency";
import { Work, WorkDraft, WorkFilters } from "../../types/works";
import { WorksFilterBar } from "../../components/works/WorksFilterBar";
import { WorksTable } from "../../components/works/WorksTable";
import { WorkFormModal } from "../../components/works/WorkFormModal";
import { WorkDetailModal } from "../../components/works/WorkDetailModal";
import { Pagination } from "../../components/ListView/Pagination";

const PAGE_SIZE = 20;

/** Converts the form's WorkDraft into the API payload (nested metric objects). */
const draftToPayload = (draft: WorkDraft): Record<string, unknown> => ({
  type: draft.type,
  customerId: draft.customerId ?? null,
  customerName: draft.customerName ?? null,
  primaryObject: draft.primaryObject,
  secondaryObject: draft.secondaryObject ?? null,
  material: draft.material ?? null,
  driverId: draft.driverId ?? null,
  vehicleId: draft.vehicleId ?? null,
  tonnage: { planned: draft.tonnagePlanned },
  distanceKm: { planned: draft.distancePlannedKm },
  hours: { planned: draft.hoursPlanned },
  roundsPlanned: draft.roundsPlanned ?? null,
  priority: draft.priority,
  remarks: draft.remarks ?? null,
  plannedStart: draft.plannedStart,
  plannedEnd: draft.plannedEnd ?? null,
  // Services are driver-owned: the dispatcher no longer adds or edits them, so we
  // never send a services array. Omitting it leaves any driver-recorded services
  // untouched on update (the backend only replaces services when the key is present).
  assignToSelf: draft.assignToSelf,
});

/**
 * Works module entry page (dispatcher console). CRUD is backed by the works
 * Redux slice + /api/works. Pages and components follow the project layout
 * (pages/, components/works/, store/slices/worksSlice).
 */
export const WorksListPage: React.FC = () => {
  const { t } = useTranslation("works");
  const dispatch = useAppDispatch();
  const { works, loading, catalog, currentWork, pagination, stats } = useAppSelector(
    (state) => state.works,
  );

  const [filters, setFilters] = useState<WorkFilters>({
    search: "",
    page: 1,
    limit: PAGE_SIZE,
  });
  // Debounced search text — keystrokes update this; it commits into `filters`
  // (resetting to page 1) after a short pause so typing doesn't spam the server.
  const [searchInput, setSearchInput] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editingWork, setEditingWork] = useState<Work | null>(null);
  const [viewingWork, setViewingWork] = useState<Work | null>(null);

  // Latest filters for the background poll, without re-subscribing the interval.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    dispatch(getCatalog());
  }, [dispatch]);

  // Server-side filtering + pagination: refetch whenever the committed filters
  // (search/type/status/page) change.
  useEffect(() => {
    dispatch(getWorks(filters));
  }, [dispatch, filters]);

  // Commit the debounced search into filters (page resets to 1 on a new term).
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 },
      );
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Drivers mutate works from their own (separate) session. That used to mean a
  // 15s poll; now the server pushes `work:changed` from worksController's
  // driverMutation chokepoint. The refetch is on the socket event rather than a
  // patch-in-place because the row's position depends on the active filters and
  // sort, which only the server resolves.
  const viewingId = viewingWork?._id;

  useSocketEvent<Work>("work:changed", (changed) => {
    if (document.hidden) return;
    dispatch(getWorks(filtersRef.current));

    // The detail modal renders a captured snapshot the list refetch doesn't
    // touch, so refresh the open work too.
    if (viewingId && changed?._id === viewingId) dispatch(getWork(viewingId));
  });

  // Kept from the old poll: free, and they cover both a socket gap and the tab
  // having been in the background.
  useEffect(() => {
    const refetch = () => {
      if (!document.hidden) dispatch(getWorks(filtersRef.current));
    };
    window.addEventListener("focus", refetch);
    document.addEventListener("visibilitychange", refetch);
    return () => {
      window.removeEventListener("focus", refetch);
      document.removeEventListener("visibilitychange", refetch);
    };
  }, [dispatch]);

  useSocketResync(() => {
    dispatch(getWorks(filtersRef.current));
    if (viewingId) dispatch(getWork(viewingId));
  });

  // Type/status chips apply immediately (resetting to page 1); the search box is
  // routed through the debounced `searchInput` above.
  const handleFilterBarChange = (next: WorkFilters) => {
    if (next.search !== searchInput) setSearchInput(next.search);
    if (next.type !== filters.type || next.status !== filters.status) {
      setFilters((prev) => ({
        ...prev,
        type: next.type,
        status: next.status,
        page: 1,
      }));
    }
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const paginationDetails = {
    page: pagination.page,
    pages: pagination.pages,
    total: pagination.total,
    hasNext: pagination.page < pagination.pages,
    hasPrev: pagination.page > 1,
  };

  const openCreate = () => {
    setFormMode("create");
    setEditingWork(null);
    setFormOpen(true);
  };

  const openEdit = (work: Work) => {
    // Completed works are read-only — never open the edit form for them.
    if (work.status === "completed") return;
    setViewingWork(null);
    setFormMode("edit");
    setEditingWork(work);
    setFormOpen(true);
  };

  const openView = (work: Work) => {
    setViewingWork(work);
    // Pull the authoritative copy so the modal reflects the driver's latest
    // progress immediately, not the (possibly stale) list snapshot.
    dispatch(getWork(work._id));
  };

  const closeView = () => {
    setViewingWork(null);
    dispatch(clearCurrentWork());
  };

  // The modal renders the freshly fetched work once it matches the open id;
  // until then it falls back to the list snapshot so it never flashes empty.
  const detailWork =
    viewingWork && currentWork?._id === viewingWork._id ? currentWork : viewingWork;

  const handleSubmit = async (draft: WorkDraft) => {
    try {
      const payload = draftToPayload(draft);
      if (formMode === "create") {
        await dispatch(createWork(payload)).unwrap();
        toast.success(t("toasts.createSuccess"));
        setSearchInput("");
        setFilters({ search: "", page: 1, limit: PAGE_SIZE });
      } else if (editingWork) {
        await dispatch(
          updateWork({
            id: editingWork._id,
            data: withVersion(payload, editingWork),
          }),
        ).unwrap();
        toast.success(t("toasts.updateSuccess"));
      }
      setFormOpen(false);
      setEditingWork(null);
    } catch (error) {
      toast.error(typeof error === "string" ? error : t("toasts.saveError"));
    }
  };

  const handleDelete = async (work: Work) => {
    if (!window.confirm(t("table.confirmDelete", { workNumber: work.workNumber }))) return;
    try {
      await dispatch(deleteWork(work._id)).unwrap();
      toast.success(t("toasts.deleteSuccess"));
      if (viewingWork?._id === work._id) closeView();
      // Refresh the current page + stat counts after removing a row.
      dispatch(getWorks(filtersRef.current));
    } catch {
      toast.error(t("toasts.deleteError"));
    }
  };

  return (
    <div className="p-3 sm:p-6 space-y-3 sm:space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-[#FCEEE2] flex items-center justify-center text-[#C97A3C] shrink-0">
            <Briefcase size={18} />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-[#0B0F0E] leading-tight">{t("page.title")}</h1>
            <p className="text-xs sm:text-sm text-[#8A9590]">{t("page.subtitle")}</p>
          </div>
        </div>
        <Button variant="primary" onClick={openCreate} icon={<Plus size={16} />}>
          {t("page.createButton")}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="bg-white border border-[#ECEAE4] rounded-xl px-3 py-2 sm:px-4 sm:py-3">
          <div className="text-xs text-[#8A9590]">{t("page.stats.total")}</div>
          <div className="text-lg sm:text-xl font-semibold text-[#2B2F2E] mt-0.5">{stats.total}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl px-3 py-2 sm:px-4 sm:py-3">
          <div className="text-xs text-[#8A9590]">{t("status.in_progress")}</div>
          <div className="text-lg sm:text-xl font-semibold text-[#C97A3C] mt-0.5">{stats.in_progress}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl px-3 py-2 sm:px-4 sm:py-3">
          <div className="text-xs text-[#8A9590]">{t("status.planned")}</div>
          <div className="text-lg sm:text-xl font-semibold text-[#3B6FB6] mt-0.5">{stats.planned}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl px-3 py-2 sm:px-4 sm:py-3">
          <div className="text-xs text-[#8A9590]">{t("status.overdue")}</div>
          <div className="text-lg sm:text-xl font-semibold text-[#E24B4A] mt-0.5">{stats.overdue}</div>
        </div>
      </div>

      <div className="bg-white border border-[#ECEAE4] rounded-xl p-3 sm:p-4">
        <WorksFilterBar
          filters={{ ...filters, search: searchInput }}
          onChange={handleFilterBarChange}
        />
      </div>

      <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
        <WorksTable
          works={works}
          loading={loading && works.length === 0}
          onView={openView}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
        <Pagination
          pagination={paginationDetails}
          onPageChange={handlePageChange}
          pageSize={filters.limit ?? PAGE_SIZE}
        />
      </div>

      <WorkFormModal
        isOpen={formOpen}
        mode={formMode}
        initialWork={editingWork}
        catalog={catalog}
        onClose={() => {
          setFormOpen(false);
          setEditingWork(null);
        }}
        onSubmit={handleSubmit}
      />

      <WorkDetailModal
        isOpen={!!viewingWork}
        work={detailWork}
        onClose={closeView}
        onEdit={openEdit}
        onDelete={handleDelete}
        onServicesChanged={() => dispatch(getWorks(filtersRef.current))}
      />
    </div>
  );
};

export default WorksListPage;
